import { useEffect, useState } from "react";
import { Wallet, ArrowDownLeft, ArrowUpRight, Send, RefreshCw, Zap } from "lucide-react";
import api, { fmtNaira } from "@/lib/api";
import { toast } from "sonner";

const SOURCE_LABELS = {
  dva: "Bank transfer",
  card: "Card payment",
  transfer_out: "Sent out",
  withdraw: "Withdrawn",
  manual_topup: "Top up",
  migration: "Older payment",
};

export default function WalletSummary() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTransfer, setShowTransfer] = useState(false);
  const [payoutAccount, setPayoutAccount] = useState(null);
  const [showPayoutSetup, setShowPayoutSetup] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [cashingOut, setCashingOut] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState(null); // {transfer_code, reference, amount}

  async function load() {
    try {
      const [s, p] = await Promise.all([
        api.get("/wallet/summary"),
        api.get("/wallet/payout/account").catch(() => ({ data: {} })),
      ]);
      setSummary(s.data);
      setPayoutAccount(p.data.payout_account || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function quickCashOut() {
    if (!payoutAccount) return setShowPayoutSetup(true);
    if (!summary?.balance || summary.balance <= 0) return;
    if (!window.confirm(`Cash out ${fmtNaira(summary.balance)} to ${payoutAccount.account_name || payoutAccount.account_number}?`)) return;
    setCashingOut(true);
    try {
      const r = await api.post("/wallet/payout/quick", {});
      if (r.data.requires_otp) {
        setOtpChallenge({ transfer_code: r.data.transfer_code, reference: r.data.reference, amount: r.data.amount });
        toast.info("Paystack sent an OTP to the account holder's phone. Enter it to confirm.");
      } else {
        toast.success(`Payout of ${fmtNaira(r.data.amount)} sent · ref ${r.data.reference.slice(-8)}`);
        load();
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || d?.code || "Cash out failed");
      toast.error(msg);
    } finally {
      setCashingOut(false);
    }
  }

  if (loading) {
    return (
      <div className="bg-white border border-border rounded-2xl p-4 text-sm text-kudi-ink/50" data-testid="wallet-summary-loading">
        Loading wallet…
      </div>
    );
  }
  if (!summary) return null;

  return (
    <div className="space-y-3" data-testid="wallet-summary">
      {/* Wallet hero */}
      <div className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-kudi-terracotta to-kudi-terracotta-dark text-white" data-testid="wallet-hero">
        <div className="absolute inset-0 noise-overlay opacity-30" aria-hidden />
        <div className="relative flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-85">
              <Wallet className="w-3.5 h-3.5" />
              Wallet balance
              <span className="ml-1 opacity-60 lowercase text-[10px] normal-case font-normal">(real money in your DVA)</span>
            </div>
            <div className="font-heading text-3xl font-bold mt-1 tabular-nums" data-testid="wallet-balance">
              {fmtNaira(summary.balance)}
            </div>
          </div>
          <button
            onClick={load}
            className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center transition"
            data-testid="wallet-refresh"
            aria-label="Refresh wallet"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        <div className="relative mt-4 grid grid-cols-2 gap-2 text-xs">
          <div className="bg-white/12 backdrop-blur rounded-lg px-3 py-2">
            <div className="flex items-center gap-1 opacity-80"><ArrowDownLeft className="w-3 h-3" /> IN THIS MONTH</div>
            <div className="font-bold mt-0.5 tabular-nums" data-testid="wallet-month-in">{fmtNaira(summary.month_in)}</div>
          </div>
          <div className="bg-white/12 backdrop-blur rounded-lg px-3 py-2">
            <div className="flex items-center gap-1 opacity-80"><ArrowUpRight className="w-3 h-3" /> OUT THIS MONTH</div>
            <div className="font-bold mt-0.5 tabular-nums" data-testid="wallet-month-out">{fmtNaira(summary.month_out)}</div>
          </div>
        </div>

        <button
          onClick={() => setShowTransfer(true)}
          className="relative mt-4 w-full bg-white/95 text-kudi-terracotta font-semibold rounded-xl py-2.5 flex items-center justify-center gap-2 hover:bg-white transition disabled:opacity-50"
          disabled={summary.balance <= 0}
          data-testid="wallet-send-btn"
        >
          <Send className="w-4 h-4" />
          {summary.balance <= 0 ? "Balance too low to send" : "Send money"}
        </button>

        {summary.balance > 0 && (
          <button
            onClick={quickCashOut}
            disabled={cashingOut}
            className="relative mt-2 w-full bg-white/15 backdrop-blur border border-white/30 text-white font-semibold rounded-xl py-2 flex items-center justify-center gap-2 hover:bg-white/25 transition disabled:opacity-50 text-sm"
            data-testid="wallet-cashout-btn"
          >
            <Zap className="w-3.5 h-3.5" />
            {cashingOut ? "Sending…" : payoutAccount
              ? `Cash out to ${payoutAccount.account_name?.split(" ")[0] || payoutAccount.account_number.slice(-4)} · one tap`
              : "Set up 1-tap cash out"}
          </button>
        )}
      </div>

      {/* Recent movements */}
      {summary.recent?.length > 0 && (
        <div className="bg-white border border-border rounded-2xl p-4" data-testid="wallet-recent">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-kudi-ink/55">Recent wallet activity</div>
            <button onClick={() => setShowHistory(true)} className="text-[11px] font-semibold text-kudi-terracotta hover:underline" data-testid="wallet-see-all">See all</button>
          </div>
          <div className="space-y-2">
            {summary.recent.map((t) => (
              <div key={t.id} className="flex items-center justify-between text-sm" data-testid={`wallet-tx-${t.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${t.direction === "in" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                    {t.direction === "in" ? <ArrowDownLeft className="w-3.5 h-3.5" /> : <ArrowUpRight className="w-3.5 h-3.5" />}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-medium">{SOURCE_LABELS[t.source] || t.source}</div>
                    <div className="text-[11px] text-kudi-ink/55 truncate">{t.description || new Date(t.at).toLocaleString("en-NG", { dateStyle: "short", timeStyle: "short" })}</div>
                  </div>
                </div>
                <div className={`font-bold tabular-nums ${t.direction === "in" ? "text-emerald-700" : "text-rose-700"}`}>
                  {t.direction === "in" ? "+" : "−"}{fmtNaira(t.amount)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showHistory && (
        <WalletHistoryModal onClose={() => setShowHistory(false)} />
      )}

      {showTransfer && (
        <TransferModal
          balance={summary.balance}
          onClose={() => setShowTransfer(false)}
          onSuccess={() => { setShowTransfer(false); load(); }}
          onOtpRequired={(c) => { setShowTransfer(false); setOtpChallenge(c); toast.info("Paystack sent an OTP. Confirm to complete the transfer."); }}
        />
      )}
      {showPayoutSetup && (
        <PayoutSetupModal
          onClose={() => setShowPayoutSetup(false)}
          onSaved={(po) => { setPayoutAccount(po); setShowPayoutSetup(false); toast.success("Saved. Tap 'Cash out' anytime."); }}
        />
      )}
      {otpChallenge && (
        <OtpChallengeModal
          challenge={otpChallenge}
          onClose={() => setOtpChallenge(null)}
          onDone={() => { setOtpChallenge(null); load(); }}
        />
      )}
    </div>
  );
}

function OtpChallengeModal({ challenge, onClose, onDone }) {
  const [otp, setOtp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!/^\d{4,10}$/.test(otp)) return toast.error("Enter the OTP");
    setSubmitting(true);
    try {
      const r = await api.post("/wallet/payout/finalize", {
        transfer_code: challenge.transfer_code,
        otp,
      });
      toast.success(`Transfer confirmed — ${fmtNaira(r.data.amount)}. New balance ${fmtNaira(r.data.new_balance)}.`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OTP verification failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function resend() {
    setResending(true);
    try {
      await api.post("/wallet/payout/resend-otp", { transfer_code: challenge.transfer_code });
      toast.success("OTP resent — check your Paystack phone number.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend OTP");
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-3" data-testid="otp-modal" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold">Confirm transfer</h3>
          <button type="button" onClick={onClose} className="text-kudi-ink/50 hover:text-kudi-ink" data-testid="otp-close">✕</button>
        </div>
        <p className="text-xs text-kudi-ink/60">
          Paystack sent a one-time code to the phone number on your Paystack account. Enter it to release{" "}
          <span className="font-bold text-kudi-ink">{fmtNaira(challenge.amount)}</span>.
        </p>
        <input
          type="text" inputMode="numeric" maxLength={10} autoFocus
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          placeholder="Enter OTP"
          className="w-full border border-border rounded-lg px-3 py-3 text-lg tracking-widest text-center focus:border-kudi-terracotta focus:outline-none"
          data-testid="otp-input"
        />
        <button
          type="submit" disabled={submitting}
          className="w-full bg-kudi-terracotta hover:bg-kudi-terracotta-dark text-white font-semibold rounded-xl py-3 disabled:opacity-50 transition"
          data-testid="otp-submit"
        >
          {submitting ? "Confirming…" : "Confirm & release funds"}
        </button>
        <button
          type="button" onClick={resend} disabled={resending}
          className="w-full text-xs text-kudi-ink/60 hover:text-kudi-terracotta"
          data-testid="otp-resend"
        >
          {resending ? "Resending…" : "Didn't get the code? Resend OTP"}
        </button>
      </form>
    </div>
  );
}

function PayoutSetupModal({ onClose, onSaved }) {
  const [accountNumber, setAccountNumber] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [accountName, setAccountName] = useState("");
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);
  const banks = [
    { code: "058", name: "GTBank" },
    { code: "057", name: "Zenith Bank" },
    { code: "044", name: "Access Bank" },
    { code: "011", name: "First Bank" },
    { code: "033", name: "UBA" },
    { code: "070", name: "Fidelity" },
    { code: "232", name: "Sterling" },
    { code: "50211", name: "Kuda" },
    { code: "50515", name: "Moniepoint" },
    { code: "999992", name: "OPay" },
    { code: "100004", name: "Palmpay" },
  ];

  // Auto-resolve on 10-digit account + bank code
  useEffect(() => {
    if (!/^\d{10}$/.test(accountNumber) || !bankCode) return;
    let cancelled = false;
    setResolving(true);
    setAccountName("");
    api
      .post("/banks/resolve", { account_number: accountNumber, bank_code: bankCode })
      .then((res) => { if (!cancelled) setAccountName(res.data.account_name || ""); })
      .catch((err) => {
        if (!cancelled) {
          const msg = err?.response?.data?.detail || "Could not verify account";
          setAccountName("");
          toast.error(typeof msg === "string" ? msg : "Could not verify account");
        }
      })
      .finally(() => { if (!cancelled) setResolving(false); });
    return () => { cancelled = true; };
  }, [accountNumber, bankCode]);

  async function submit(e) {
    e.preventDefault();
    if (!/^\d{10}$/.test(accountNumber)) return toast.error("Enter a 10-digit account number");
    if (!bankCode) return toast.error("Pick a bank");
    setSaving(true);
    try {
      const r = await api.post("/wallet/payout/account", {
        account_number: accountNumber, bank_code: bankCode, account_name: accountName || undefined,
      });
      onSaved(r.data.payout_account);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-3" data-testid="payout-setup-modal" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold">Set up 1-tap cash out</h3>
          <button type="button" onClick={onClose} className="text-kudi-ink/50 hover:text-kudi-ink" data-testid="payout-setup-close">✕</button>
        </div>
        <p className="text-xs text-kudi-ink/60">Save your personal bank account once — then a single tap moves your wallet balance to your bank.</p>

        <input type="text" inputMode="numeric" maxLength={10} required
          value={accountNumber} onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
          placeholder="10-digit account number *"
          className="w-full border border-border rounded-lg px-3 py-2.5 focus:border-kudi-terracotta focus:outline-none"
          data-testid="payout-account-number"
        />

        <select value={bankCode} onChange={(e) => setBankCode(e.target.value)} required
          className="w-full border border-border rounded-lg px-3 py-2.5 focus:border-kudi-terracotta focus:outline-none"
          data-testid="payout-bank">
          <option value="">Select bank *</option>
          {banks.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
        </select>

        <input type="text" value={accountName} onChange={(e) => setAccountName(e.target.value)}
          placeholder={resolving ? "Verifying account…" : "Account holder name (auto-filled after picking bank)"}
          readOnly={resolving}
          className="w-full border border-border rounded-lg px-3 py-2.5 focus:border-kudi-terracotta focus:outline-none disabled:bg-gray-50"
          data-testid="payout-account-name" />

        <button type="submit" disabled={saving}
          className="w-full bg-kudi-terracotta hover:bg-kudi-terracotta-dark text-white font-semibold rounded-xl py-3 disabled:opacity-50 transition"
          data-testid="payout-save">
          {saving ? "Saving…" : "Save & enable cash out"}
        </button>
      </form>
    </div>
  );
}

function TransferModal({ balance, onClose, onSuccess, onOtpRequired }) {
  const [amount, setAmount] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [narration, setNarration] = useState("");
  const [sending, setSending] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [banks, setBanks] = useState([]);

  useEffect(() => {
    // Static shortlist so the modal is usable even without a /banks endpoint.
    setBanks([
      { code: "058", name: "GTBank" },
      { code: "057", name: "Zenith Bank" },
      { code: "044", name: "Access Bank" },
      { code: "011", name: "First Bank" },
      { code: "033", name: "UBA" },
      { code: "070", name: "Fidelity" },
      { code: "232", name: "Sterling" },
      { code: "032", name: "Union Bank" },
      { code: "214", name: "First City Monument" },
      { code: "221", name: "Stanbic IBTC" },
      { code: "215", name: "Unity Bank" },
      { code: "50211", name: "Kuda" },
      { code: "50515", name: "Moniepoint" },
      { code: "999992", name: "OPay" },
      { code: "100004", name: "Palmpay" },
    ]);
  }, []);

  // Auto-resolve recipient name once both account_number (10 digits) and bank_code are set.
  useEffect(() => {
    if (!/^\d{10}$/.test(accountNumber) || !bankCode) return;
    let cancelled = false;
    setResolving(true);
    setRecipientName("");
    api
      .post("/banks/resolve", { account_number: accountNumber, bank_code: bankCode })
      .then((res) => { if (!cancelled) setRecipientName(res.data.account_name || ""); })
      .catch((err) => {
        if (!cancelled) {
          const msg = err?.response?.data?.detail || "Could not verify account";
          setRecipientName("");
          toast.error(typeof msg === "string" ? msg : "Could not verify account");
        }
      })
      .finally(() => { if (!cancelled) setResolving(false); });
    return () => { cancelled = true; };
  }, [accountNumber, bankCode]);

  async function submit(e) {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (amt > balance) return toast.error(`Balance ${fmtNaira(balance)} is not enough`);
    if (!/^\d{10}$/.test(accountNumber)) return toast.error("Enter a 10-digit account number");
    if (!bankCode) return toast.error("Pick a bank");

    setSending(true);
    try {
      const r = await api.post("/wallet/transfer", {
        amount: amt,
        recipient_account_number: accountNumber,
        recipient_bank_code: bankCode,
        recipient_name: recipientName || undefined,
        narration: narration || undefined,
      });
      if (r.data.requires_otp) {
        onOtpRequired({ transfer_code: r.data.transfer_code, reference: r.data.reference, amount: amt });
        return;
      }
      toast.success(`Transfer sent — ${fmtNaira(amt)}. Ref: ${r.data.reference.slice(-8)}`);
      onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || detail?.code || "Transfer failed");
      toast.error(msg);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-3" data-testid="transfer-modal" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 shadow-2xl"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold">Send money</h3>
          <button type="button" onClick={onClose} className="text-kudi-ink/50 hover:text-kudi-ink" data-testid="transfer-close">✕</button>
        </div>
        <p className="text-xs text-kudi-ink/60">Available: <span className="font-bold text-kudi-ink">{fmtNaira(balance)}</span></p>

        <label className="block">
          <span className="text-xs font-medium text-kudi-ink/70">Amount (₦)</span>
          <input
            type="number" min="1" step="1"
            value={amount} onChange={(e) => setAmount(e.target.value)}
            className="mt-1 w-full border border-border rounded-lg px-3 py-2.5 text-lg font-bold tabular-nums focus:border-kudi-terracotta focus:outline-none"
            placeholder="0"
            data-testid="transfer-amount"
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-kudi-ink/70">Recipient account number</span>
          <input
            type="text" inputMode="numeric" pattern="\d{10}" maxLength={10}
            value={accountNumber} onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
            className="mt-1 w-full border border-border rounded-lg px-3 py-2 focus:border-kudi-terracotta focus:outline-none"
            placeholder="10-digit account number"
            data-testid="transfer-account"
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-kudi-ink/70">Bank</span>
          <select
            value={bankCode} onChange={(e) => setBankCode(e.target.value)}
            className="mt-1 w-full border border-border rounded-lg px-3 py-2 focus:border-kudi-terracotta focus:outline-none"
            data-testid="transfer-bank"
          >
            <option value="">Select bank…</option>
            {banks.map((b) => (
              <option key={b.code} value={b.code}>{b.name}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-kudi-ink/70">
            Recipient name {resolving && <span className="text-kudi-terracotta">· verifying…</span>}
            {!resolving && recipientName && /^\d{10}$/.test(accountNumber) && bankCode && (
              <span className="text-green-700">· verified ✓</span>
            )}
          </span>
          <input
            type="text" value={recipientName} onChange={(e) => setRecipientName(e.target.value)}
            className="mt-1 w-full border border-border rounded-lg px-3 py-2 focus:border-kudi-terracotta focus:outline-none disabled:bg-gray-50"
            placeholder={resolving ? "Verifying…" : "Auto-filled after picking bank"}
            readOnly={resolving}
            data-testid="transfer-name"
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-kudi-ink/70">Note (optional)</span>
          <input
            type="text" maxLength={80}
            value={narration} onChange={(e) => setNarration(e.target.value)}
            className="mt-1 w-full border border-border rounded-lg px-3 py-2 focus:border-kudi-terracotta focus:outline-none"
            placeholder="e.g. Stock top-up"
            data-testid="transfer-narration"
          />
        </label>

        <button
          type="submit" disabled={sending}
          className="w-full bg-kudi-terracotta hover:bg-kudi-terracotta-dark text-white font-semibold rounded-xl py-3 mt-1 disabled:opacity-50 transition"
          data-testid="transfer-submit"
        >
          {sending ? "Sending…" : `Send ${amount ? fmtNaira(Number(amount)) : "money"}`}
        </button>
      </form>
    </div>
  );
}



/** Full wallet history + payments received tabs */
function WalletHistoryModal({ onClose }) {
  const [tab, setTab] = useState("wallet");
  const [wallet, setWallet] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/wallet/transactions?limit=200").catch(() => ({ data: { items: [] } })),
      api.get("/payments/list").catch(() => ({ data: { payments: [] } })),
    ]).then(([w, p]) => {
      if (cancelled) return;
      setWallet(w.data.items || w.data.transactions || []);
      setPayments(p.data.payments || []);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="wallet-history-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg h-[92vh] sm:h-[80vh] sm:rounded-3xl rounded-t-3xl flex flex-col shadow-2xl">
        <header className="p-4 border-b border-border flex items-center gap-2 shrink-0">
          <h3 className="font-heading font-bold flex-1">Wallet history</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-kudi-cream flex items-center justify-center" data-testid="history-close">✕</button>
        </header>

        <div className="px-3 pt-2 border-b border-border flex gap-1 shrink-0">
          {[
            { k: "wallet", l: "All movements", n: wallet.length },
            { k: "payments", l: "Payments received", n: payments.length },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`flex-1 py-2 text-xs font-semibold border-b-2 transition ${tab === t.k ? "border-kudi-terracotta text-kudi-terracotta" : "border-transparent text-kudi-ink/50"}`}
              data-testid={`history-tab-${t.k}`}
            >
              {t.l} <span className="opacity-70">({t.n})</span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-3 bg-kudi-cream/30">
          {loading ? (
            <div className="p-8 text-center text-kudi-ink/40">Loading…</div>
          ) : tab === "wallet" ? (
            wallet.length === 0 ? (
              <div className="p-8 text-center text-kudi-ink/50">No wallet transactions yet.</div>
            ) : (
              <ul className="space-y-2">
                {wallet.map((t) => (
                  <li key={t.id || t.reference} className="bg-white border border-border rounded-xl p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate capitalize">{SOURCE_LABELS[t.source] || t.source}</div>
                      <div className="text-[11px] text-kudi-ink/55 truncate">{t.description || t.reference}</div>
                      <div className="text-[10px] text-kudi-ink/40 mt-0.5">{t.at ? new Date(t.at).toLocaleString("en-NG") : ""}</div>
                    </div>
                    <div className={`font-bold tabular-nums shrink-0 ${t.direction === "in" ? "text-emerald-700" : "text-rose-700"}`}>
                      {t.direction === "in" ? "+" : "−"}{fmtNaira(t.amount)}
                    </div>
                  </li>
                ))}
              </ul>
            )
          ) : (
            payments.length === 0 ? (
              <div className="p-8 text-center text-kudi-ink/50">No payment links generated yet.</div>
            ) : (
              <ul className="space-y-2">
                {payments.map((p) => (
                  <li key={p.reference || p.id} className="bg-white border border-border rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-sm font-semibold truncate">{p.customer_name || p.description || "Payment"}</div>
                      <div className="font-bold text-kudi-green tabular-nums shrink-0">{fmtNaira(p.amount)}</div>
                    </div>
                    <div className="text-[11px] text-kudi-ink/55 flex items-center gap-2 flex-wrap">
                      <span className={`px-1.5 py-0.5 rounded ${p.status === "success" ? "bg-emerald-50 text-emerald-700" : p.status === "failed" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{p.status || "pending"}</span>
                      {p.reference && <span className="font-mono truncate">{p.reference}</span>}
                      {p.created_at && <span>· {new Date(p.created_at).toLocaleDateString("en-NG")}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )
          )}
        </div>
      </div>
    </div>
  );
}
